import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import CameraCapture from '../components/CameraCapture';
import { FaCamera, FaTimes, FaChevronDown, FaCar, FaFileAlt, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { authAPI, vehiclesAPI, crashReportsAPI, optionsAPI, apiHelpers } from '../api/api';
import { toast } from 'react-toastify';
import '../css/CrashReport.css';

const CrashReport = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    vehicleId: '',
    chassisNumber: '',
    damageDescription: '',
    reportReason: '',
    notes: ''
  });
  const [photos, setPhotos] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isVehicleDropdownOpen, setIsVehicleDropdownOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [user, setUser] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [reportReasons, setReportReasons] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS device
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
  }, []);

  useEffect(() => {
    const currentUser = authAPI.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setFormData(prev => ({
        ...prev,
        fullName: currentUser.name
      }));

      fetchVehicles();
      fetchReportReasons();
    }
  }, []);

  const fetchVehicles = async () => {
    try {
      const clientUserId = authAPI.getCurrentUserId();
      if (clientUserId) {
        const vehiclesData = await vehiclesAPI.getClientCurrentVehicles(clientUserId);
        setVehicles(vehiclesData);
      }
    } catch (error) {
      console.error('Hiba a járművek lekérésekor:', error);
      toast.error('Hiba a járművek lekérésekor');
    }
  };

  const fetchReportReasons = async () => {
    try {
      const reasonsData = await optionsAPI.getReportReasonOptions();
      setReportReasons(reasonsData);
    } catch (error) {
      console.error('Hiba a bejelentés okok lekérésekor:', error);
      toast.error('Hiba a bejelentés okok lekérésekor');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReasonSelect = (reason) => {
    setSelectedReason(reason.label);
    setFormData(prev => ({
      ...prev,
      reportReason: reason.value
    }));
    setIsDropdownOpen(false);
  };

  const handleVehicleSelect = (vehicle) => {
    const licensePlate = vehicle.license_plate || vehicle.chassis_number17 || vehicle.chassis_number || '';
    setSelectedVehicle(`${vehicle.brand || ''} ${vehicle.model || ''} - ${licensePlate}`.trim());
    setFormData(prev => ({
      ...prev,
      vehicleId: vehicle.id,
      chassisNumber: vehicle.chassis_number17 || vehicle.chassis_number
    }));
    setIsVehicleDropdownOpen(false);
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      preview: URL.createObjectURL(file)
    }));
    setPhotos(prev => [...prev, ...newPhotos].slice(0, 6));
  };

  const handleCameraPhotoCapture = (blob) => {
    const file = new File([blob], `camera_photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
    const newPhoto = {
      id: Date.now() + Math.random(),
      file: file,
      preview: URL.createObjectURL(file)
    };
    setPhotos(prev => [...prev, newPhoto].slice(0, 6));
    setShowCamera(false);
  };

  const handleCameraClose = () => {
    setShowCamera(false);
  };

  const handleCameraRetake = () => {
    setShowCamera(false);
    setTimeout(() => {
      setShowCamera(true);
    }, 100);
  };

  const handleCameraOpen = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Kamera nem támogatott ezen a böngészőben. Kérjük, használja a "Fénykép feltöltése" funkciót.');
      return;
    }
    
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      if (videoDevices.length === 0) {
        alert('Nem található kamera az eszközön. Kérjük, ellenőrizze a webkamera csatlakozását.');
        return;
      }
      
      setShowCamera(true);
    } catch (error) {
      setShowCamera(true);
    }
  };

  const removePhoto = (id) => {
    setPhotos(prev => prev.filter(photo => photo.id !== id));
  };

  const createCrashReportData = () => {
    const clientUserId = authAPI.getCurrentUserId();

    if (photos.length > 0) {
      const formDataToSend = new FormData();

      formDataToSend.append('client_user_id', clientUserId);
      formDataToSend.append('company_id', '1');
      formDataToSend.append('vehicle_id', formData.vehicleId);
      formDataToSend.append('chassis_number', formData.chassisNumber);
      formDataToSend.append('damage_description', formData.damageDescription);
      formDataToSend.append('report_reason', formData.reportReason);
      formDataToSend.append('notes', formData.notes || '');

      photos.forEach((photo, index) => {
        formDataToSend.append('photos[]', photo.file);
      });

      return formDataToSend;
    }

    return {
      client_user_id: clientUserId,
      company_id: 1,
      vehicle_id: formData.vehicleId,
      chassis_number: formData.chassisNumber,
      damage_description: formData.damageDescription,
      report_reason: formData.reportReason,
      notes: formData.notes || ''
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const crashReportData = createCrashReportData();

      const result = await crashReportsAPI.createCrashReport(crashReportData);

      toast.success('Kárbejelentés sikeresen elküldve!');
      handleCancel();
    } catch (error) {
      console.error('Hiba a kárbejelentés elküldésekor:', error);
      toast.error(`Hiba a kárbejelentés elküldésekor: ${apiHelpers.handleApiError(error)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: user ? user.name : '',
      vehicleId: '',
      chassisNumber: '',
      damageDescription: '',
      reportReason: '',
      notes: ''
    });
    setPhotos([]);
    setSelectedReason('');
    setSelectedVehicle('');
  };

  return (
    <div className="crash-report-page">
      <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
      <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
      
      <div className="page-content">
        <div className="crash-report-container">
          <div className="crash-report-header">
            <div className="crash-report-title-section">
              <FaExclamationTriangle className="crash-report-icon" />
              <h1>Kárbejelentő</h1>
              <p className="crash-report-subtitle">Jelentse be a járművével kapcsolatos károkat és hibákat</p>
            </div>
          </div>


          <div className="crash-report-content">
            <form className="crash-report-form" onSubmit={handleSubmit}>
              <div className="form-section">
                <div className="section-header">
                  <FaCar className="section-icon" />
                  <h3>Jármű adatok</h3>
                </div>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="fullName">Teljes név</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Adja meg a teljes nevét"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Jármű kiválasztása</label>
                    <div className="dropdown-container">
                      <div 
                        className="dropdown-select"
                        onClick={() => setIsVehicleDropdownOpen(!isVehicleDropdownOpen)}
                      >
                        <span className={selectedVehicle ? 'selected' : 'placeholder'}>
                          {selectedVehicle || 'Válasszon járművet'}
                        </span>
                        <FaChevronDown className="dropdown-arrow" />
                      </div>
                      {isVehicleDropdownOpen && (
                        <div className="dropdown-options">
                          {vehicles.length > 0 ? (
                            vehicles.map((vehicle) => (
                              <div
                                key={vehicle.id}
                                className={`dropdown-option ${formData.vehicleId === vehicle.id ? 'selected' : ''}`}
                                onClick={() => handleVehicleSelect(vehicle)}
                              >
                                {vehicle.name}
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-option disabled">
                              Nincs elérhető jármű
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <input
                      type="text"
                      name="chassisNumber"
                      value={formData.chassisNumber}
                      readOnly
                      placeholder="Alvázszám (automatikus)"
                      className="readonly-input"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <FaExclamationTriangle className="section-icon" />
                  <h3>Kár leírása</h3>
                </div>
                
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label htmlFor="damageDescription">Hiba / Kár leírása</label>
                    <textarea
                      id="damageDescription"
                      name="damageDescription"
                      value={formData.damageDescription}
                      onChange={handleInputChange}
                      placeholder="Részletesen írja le a hibát vagy kárt..."
                      rows="4"
                      maxLength={300}
                      required
                    />
                    <div className="character-counter">
                      {formData.damageDescription.length}/300 karakter
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Bejelentés oka</label>
                    <div className="dropdown-container">
                      <div 
                        className="dropdown-select"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      >
                        <span className={selectedReason ? 'selected' : 'placeholder'}>
                          {selectedReason || 'Válasszon okot'}
                        </span>
                        <FaChevronDown className="dropdown-arrow" />
                      </div>
                      {isDropdownOpen && (
                        <div className="dropdown-options">
                          {reportReasons.map((reason) => (
                            <div
                              key={reason.value}
                              className={`dropdown-option ${formData.reportReason === reason.value ? 'selected' : ''}`}
                              onClick={() => handleReasonSelect(reason)}
                            >
                              {reason.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes">Megjegyzések</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="További megjegyzések (opcionális)"
                      rows="3"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <FaCamera className="section-icon" />
                  <h3>Fotó dokumentáció</h3>
                </div>
                
                <div className="photo-section">
                  <div className="photo-upload-area">
                    <div className="photo-upload-circle">
                      <input
                        type="file"
                        id="mainPhoto"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        multiple
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="mainPhoto" className="photo-upload-label">
                        <FaCamera className="camera-icon" />
                        <span>Fénykép feltöltése</span>
                      </label>
                    </div>
                    
                    <div className="photo-upload-buttons">
                      <button
                        type="button"
                        className="btn-photo-upload"
                        onClick={() => document.getElementById('mainPhoto').click()}
                      >
                        <FaFileAlt />
                        Kép kiválasztása
                      </button>
                      {!isIOS && (
                        <button
                          type="button"
                          className="btn-camera-capture"
                          onClick={handleCameraOpen}
                        >
                          <FaCamera />
                          Kamera
                        </button>
                      )}
                    </div>
                    
                    <p className="photo-instruction">
                      Töltsön fel minél részletesebb képeket a bejelenteni kívánt eseményről
                    </p>
                  </div>

                  <div className="photo-grid">
                    {Array.from({ length: 6 }, (_, index) => (
                      <div key={index} className="photo-placeholder">
                        {photos[index] ? (
                          <div className="photo-preview">
                            <img src={photos[index].preview} alt={`Photo ${index + 1}`} />
                            <button
                              type="button"
                              className="remove-photo"
                              onClick={() => removePhoto(photos[index].id)}
                              title="Fotó eltávolítása"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ) : (
                          <div className="photo-placeholder-content">
                            <FaCamera className="camera-icon-small" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCancel} disabled={isSubmitting}>
                  Mégse
                </button>
                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <div className="loading-spinner"></div>
                      Küldés...
                    </>
                  ) : (
                    'Kárbejelentés elküldése'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {showCamera && (
        <CameraCapture
          onPhotoCapture={handleCameraPhotoCapture}
          onClose={handleCameraClose}
          onRetake={handleCameraRetake}
        />
      )}
    </div>
  );
};

export default CrashReport;
